import React, { useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";
import { Badge } from "../components/ui/badge";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "../components/ui/alert-dialog";
import { SCStatusBadge } from "../components/etams/StatusBadge";
import { Plus, ShieldCheck, ShieldOff, XCircle } from "lucide-react";
import { toast } from "sonner";
import { formatDateTime } from "../lib/format";
import { useDaftar } from "../hooks/useDaftar";
import { PalangDaftar, ThUrut, Paginasi } from "../components/etams/PalangDaftar";
import PemilihCari from "../components/etams/PemilihCari";
import { cariCustomer, labelCustomer, ketCustomer } from "../lib/sumberPilihan";
import { konfirmasi } from "../lib/dialogAplikasi";
import { apiErrorMessage } from "../lib/inventoryApi";
import Hint from "../components/etams/Hint";

const KOLOM_CSV = [
  { kunci: "customer", judul: "Customer", ambil: (sc) => sc.customer?.name || "" },
  { kunci: "phone", judul: "Kontak", ambil: (sc) => sc.customer?.phone || "" },
  { kunci: "allowed_exception_types", judul: "Pengecualian", ambil: (sc) => (sc.allowed_exception_types || []).map((c) => EXCEPTIONS.find((x) => x.code === c)?.label || c).join(" | ") },
  { kunci: "reason", judul: "Alasan" },
  { kunci: "status", judul: "Status" },
  { kunci: "created_at", judul: "Dibuat", ambil: (sc) => formatDateTime(sc.created_at) },
];

const EXCEPTIONS = [
  { code: "NO_TANDA_JADI", label: "Tanpa Tanda Jadi" },
  { code: "NO_DP", label: "Tanpa DP" },
  { code: "DIRECT_PELUNASAN", label: "Langsung Pelunasan" },
  { code: "PICKUP_BEFORE_LUNAS", label: "Ambil Barang Sebelum Lunas" },
  { code: "DELAYED_PELUNASAN", label: "Pelunasan Tertunda" },
];

// KONTRAS-01 Batch 8 — peta `STATUS_BADGE` lokal yang dulu di sini (4 status
// dengan `cls:` warna keras) pindah ke `SC_STATUS_TONE` di
// `components/etams/StatusBadge.jsx`. Label tidak berubah sedikit pun; yang
// berubah cuma sumber warnanya, dari palet keras jadi token tema.

export default function SpecialCustomers() {
  const { isOwner, isKepalaCabang } = useAuth();
  const canPropose = isOwner || isKepalaCabang;
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState("ALL");
  const [showForm, setShowForm] = useState(false);
  const [detail, setDetail] = useState(null);
  const [rejectingId, setRejectingId] = useState(null);
  const [rejectReason, setRejectReason] = useState("");

  const [total, setTotal] = useState(0);
  // Cermin `URUT_SAH` di routers/special_customers.py. Nama customer datang
  // dari sumber tertanam, bukan kolom — nol bisa diurut server.
  const daftar = useDaftar(items, {
    server: { total, urutSah: ["created_at", "approval_status"] },
    urutAwal: { kunci: "created_at", arah: "desc" },
  });
  const { cari: q, limit, offset, sort, arah } = daftar.paramServer;

  const bangunParams = useCallback((ovr = {}) => {
    const p = { limit, offset, sort, arah, ...ovr };
    if (filterStatus !== "ALL") p.approval_status = filterStatus;
    if (q) p.cari = q;
    return p;
  }, [filterStatus, q, limit, offset, sort, arah]);

  const ambilHalaman = useCallback(async (off, n) => {
    const r = await api.get("/special-customers", { params: bangunParams({ offset: off, limit: n }) });
    return r.data;
  }, [bangunParams]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // ⛔ Dulu daftar customer ikut diambil di sini TANPA parameter — berhenti di 50
      //    customer pertama; formulir pengajuan tidak bisa memilih customer ke-51 dst.
      //    Sekarang customer dicari di server lewat PemilihCari (27 Sep 2026).
      const sc = await api.get("/special-customers", { params: bangunParams() });
      setItems(sc.data.items || []);
      setTotal(typeof sc.data.total === "number" ? sc.data.total : (sc.data.items || []).length);
    } catch (e) { toast.error(apiErrorMessage(e, "Data special customer gagal dimuat")); }
    finally { setLoading(false); }
  }, [bangunParams]);
  useEffect(() => { load(); }, [load]);
  const approve = async (sc) => {
    try { await api.patch(`/special-customers/${sc.id}`, { approval_status: "APPROVED" }); toast.success("Disetujui"); load(); }
    catch (e) { toast.error(apiErrorMessage(e, "Gagal menyetujui")); }
  };
  const submitReject = async () => {
    if (!rejectReason.trim()) { toast.error("Alasan wajib"); return; }
    try { await api.patch(`/special-customers/${rejectingId}`, { approval_status: "REJECTED", rejection_reason: rejectReason }); toast.success("Ditolak"); setRejectingId(null); setRejectReason(""); load(); }
    catch (e) { toast.error(apiErrorMessage(e, "Gagal menolak")); }
  };
  const revoke = async (sc) => {
    if (!(await konfirmasi(`Cabut status special customer untuk "${sc.customer?.name}"?`, { bahaya: true }))) return;
    try { await api.post(`/special-customers/${sc.id}/revoke`); toast.success("Dicabut"); load(); }
    catch (e) { toast.error(apiErrorMessage(e, "Gagal mencabut")); }
  };

  return (
    <div className="space-y-6" data-testid="special-customers-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
            <ShieldCheck size={22} /> Special Customers
            <Hint>Customer dengan izin pengecualian (tanpa DP/tanda jadi/lunas). Wajib persetujuan Owner.</Hint>
          </h1>
        </div>
        {canPropose && <Button onClick={() => setShowForm(true)} data-testid="sc-new-btn"><Plus size={16} className="mr-1" /> Ajukan Special Customer</Button>}
      </div>
      <Card className="p-4">
        <div className="flex flex-wrap gap-3 mb-4">
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="w-56" data-testid="sc-filter-status"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL">Semua Status</SelectItem>
              <SelectItem value="PENDING">Menunggu Owner</SelectItem>
              <SelectItem value="APPROVED">Disetujui</SelectItem>
              <SelectItem value="REJECTED">Ditolak</SelectItem>
              <SelectItem value="INACTIVE">Dicabut</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <PalangDaftar daftar={daftar} placeholder="Cari customer / kontak / alasan…" testId="sc-palang"
                      ekspor={{ nama: "customer_khusus", kolom: KOLOM_CSV, ambilHalaman }} />
        {loading ? <div className="py-8 text-center text-sm text-muted-foreground">Memuat...</div> : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/50 text-muted-foreground text-xs uppercase">
                <tr>
                  <ThUrut daftar={daftar} kunci="customer" className="text-left px-3 py-2">Customer</ThUrut>
                  <th className="text-left px-3 py-2">Kontak</th>
                  <th className="text-left px-3 py-2">Pengecualian</th>
                  <th className="text-left px-3 py-2">Alasan</th>
                  <ThUrut daftar={daftar} kunci="approval_status" className="text-left px-3 py-2">Status</ThUrut>
                  <th className="px-3 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {daftar.total === 0 && <tr><td colSpan={6} className="px-3 py-8 text-center text-muted-foreground">
                  {daftar.cari.trim() ? "Tidak ada yang cocok." : "Belum ada entry."}
                </td></tr>}
                {daftar.tampil.map((sc) => {
                  return (
                    <tr key={sc.id} className="border-t border-border" data-testid={`sc-row-${sc.id}`}>
                      <td className="px-3 py-2 font-medium">{sc.customer?.name || "—"}</td>
                      <td className="px-3 py-2 text-xs text-muted-foreground">{sc.customer?.phone || "—"}</td>
                      <td className="px-3 py-2">
                        <div className="flex flex-wrap gap-1">
                          {(sc.allowed_exception_types || []).map((e) => (
                            <Badge key={e} variant="outline" className="text-[10px]">{EXCEPTIONS.find(x => x.code === e)?.label || e}</Badge>
                          ))}
                        </div>
                      </td>
                      <td className="px-3 py-2 text-xs text-muted-foreground max-w-xs truncate">{sc.reason}</td>
                      <td className="px-3 py-2"><SCStatusBadge value={sc.status} /></td>
                      <td className="px-3 py-2 text-right whitespace-nowrap">
                        <Button variant="ghost" size="sm" onClick={() => setDetail(sc)} data-testid={`sc-detail-${sc.id}`}>Detail</Button>
                        {isOwner && sc.status === "PENDING_OWNER_APPROVAL" && (
                          <>
                            <Button variant="ghost" size="sm" onClick={() => approve(sc)} className="text-success-soft-foreground" data-testid={`sc-approve-${sc.id}`}>✓ Setujui</Button>
                            <Button variant="ghost" size="sm" onClick={() => setRejectingId(sc.id)} className="text-danger-soft-foreground" data-testid={`sc-reject-${sc.id}`}>✕ Tolak</Button>
                          </>
                        )}
                        {isOwner && sc.status === "APPROVED" && (
                          <Button variant="ghost" size="sm" onClick={() => revoke(sc)} className="text-muted-foreground" data-testid={`sc-revoke-${sc.id}`}><ShieldOff size={12} className="mr-1" />Cabut</Button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <Paginasi daftar={daftar} testId="sc-paginasi" />
          </div>
        )}
      </Card>

      {showForm && <SCFormModal onClose={() => setShowForm(false)} onSaved={() => { setShowForm(false); load(); }} />}
      {detail && <SCDetailModal sc={detail} onClose={() => setDetail(null)} />}
      <AlertDialog open={!!rejectingId} onOpenChange={(o) => { if (!o) { setRejectingId(null); setRejectReason(""); } }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Tolak Special Customer?</AlertDialogTitle>
            <AlertDialogDescription>Berikan alasan penolakan. Alasan akan tercatat di audit log.</AlertDialogDescription>
          </AlertDialogHeader>
          <Textarea rows={3} value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} placeholder="Alasan penolakan..." data-testid="sc-reject-reason" />
          <AlertDialogFooter>
            <AlertDialogCancel>Batal</AlertDialogCancel>
            <AlertDialogAction onClick={submitReject} className="bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid="sc-reject-submit">Tolak</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function SCFormModal({ onClose, onSaved }) {
  const [f, setF] = useState({ customer_id: "", reason: "", exceptions: [], notes: "" });
  const [customer, setCustomer] = useState(null);
  const [saving, setSaving] = useState(false);
  const toggle = (code) => setF((x) => ({ ...x, exceptions: x.exceptions.includes(code) ? x.exceptions.filter((e) => e !== code) : [...x.exceptions, code] }));
  const save = async () => {
    if (!f.customer_id || !f.reason.trim() || f.exceptions.length === 0) { toast.error("Customer, alasan, dan minimal 1 pengecualian wajib diisi"); return; }
    setSaving(true);
    try {
      await api.post("/special-customers", { customer_id: f.customer_id, reason: f.reason.trim(), allowed_exception_types: f.exceptions, notes: f.notes || null });
      toast.success("Diajukan (menunggu Owner)"); onSaved();
    } catch (e) { toast.error(apiErrorMessage(e, "Pengajuan gagal dikirim")); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent data-testid="sc-form-modal">
        <DialogHeader><DialogTitle>Ajukan Special Customer</DialogTitle></DialogHeader>
        <div className="space-y-3 py-2">
          <div>
            <Label>Customer *</Label>
            <PemilihCari
              value={customer}
              onChange={(c) => { setCustomer(c); setF((x) => ({ ...x, customer_id: c?.id || "" })); }}
              cari={cariCustomer}
              label={labelCustomer}
              keterangan={ketCustomer}
              placeholder="Pilih customer"
              placeholderCari="Ketik nama atau nomor telepon…"
              testId="sc-form-customer"
            />
          </div>
          <div>
            <Label>Jenis Pengecualian * (pilih ≥ 1)</Label>
            <div className="grid grid-cols-2 gap-2 mt-2">
              {EXCEPTIONS.map((e) => (
                <label key={e.code} className="flex items-center gap-2 cursor-pointer p-2 border rounded-md text-sm">
                  <Checkbox checked={f.exceptions.includes(e.code)} onCheckedChange={() => toggle(e.code)} data-testid={`sc-form-exc-${e.code}`} /> {e.label}
                </label>
              ))}
            </div>
          </div>
          <div><Label>Alasan Bisnis *</Label><Textarea rows={2} value={f.reason} onChange={(e) => setF({ ...f, reason: e.target.value })} placeholder="cth: Relasi lama Owner, sudah 3 tahun kerja sama..." data-testid="sc-form-reason" /></div>
          <div><Label>Catatan</Label><Textarea rows={2} value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} /></div>
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose}>Batal</Button><Button onClick={save} disabled={saving} data-testid="sc-form-save">{saving ? "..." : "Ajukan"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SCDetailModal({ sc, onClose }) {
  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent data-testid="sc-detail-modal">
        <DialogHeader><DialogTitle>Detail Special Customer</DialogTitle></DialogHeader>
        <div className="space-y-2 text-sm">
          <div><strong>Customer:</strong> {sc.customer?.name} · {sc.customer?.phone}</div>
          <div><strong>Status:</strong> <SCStatusBadge value={sc.status} /></div>
          <div><strong>Pengecualian:</strong> <div className="mt-1 flex flex-wrap gap-1">{(sc.allowed_exception_types || []).map((e) => <Badge key={e} variant="outline" className="text-xs">{EXCEPTIONS.find(x => x.code === e)?.label || e}</Badge>)}</div></div>
          <div><strong>Alasan:</strong> {sc.reason}</div>
          {sc.notes && <div><strong>Catatan:</strong> {sc.notes}</div>}
          {sc.rejection_reason && <div className="text-danger-soft-foreground"><strong>Alasan Penolakan:</strong> {sc.rejection_reason}</div>}
          <div className="text-xs text-muted-foreground mt-2">Diajukan: {formatDateTime(sc.created_at)}</div>
          {sc.approved_at && <div className="text-xs text-muted-foreground">Diproses: {formatDateTime(sc.approved_at)}</div>}
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose}>Tutup</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
